import React, { ChangeEvent } from "react";
import { RouteComponentPropsHistory } from "../../types";

import { DateTime } from "luxon";

import DelayInput from "../DelayInput";

import { V2, apiGet } from "../../Api";
import { formatDateLocal, formatName, truncateEllipse } from "../../Utilities";
import withTitle from "../withTitle";

interface IAppointmentInsuranceListViewState {
    isLoading: boolean | string;

    appointments: any[];
    locations: any[];
    type: string;

    searchOffset: number;
    searchLocation: string;
    searchStart: string;
    searchEnd: string;
    searchText: string;
    searchStatusWcas: string;
    searchStatusSurgeon: string;

    [name: string]: any;
}

const PAGE_SIZE = 50;

class AppointmentInsuranceListView extends React.Component<RouteComponentPropsHistory<any, any>, IAppointmentInsuranceListViewState> {
    constructor(props: Readonly<RouteComponentPropsHistory<any, any>>) {
        super(props);

        const state: IAppointmentInsuranceListViewState = {
            isLoading: true,
            type: props.match.params.type,

            appointments: [],
            locations: [],
            searchOffset: (props.location.state && props.location.state.searchOffset) || 0,
            searchLocation: (props.location.state && props.location.state.searchLocation) || "",
            searchStart: (props.location.state && props.location.state.searchStart) || "",
            searchEnd: (props.location.state && props.location.state.searchEnd) || "",
            searchText: (props.location.state && props.location.state.searchText) || "",
            searchStatusWcas: (props.location.state && props.location.state.searchStatusWcas) || "",
            searchStatusSurgeon: (props.location.state && props.location.state.searchStatusSurgeon) || "",
        };

        this.state = state;

        this.handleElementChange = this.handleElementChange.bind(this);
        this.nextPage = this.nextPage.bind(this);
        this.previousPage = this.previousPage.bind(this);
    }

    public async componentDidMount() {
        try {
            const locations = await V2.get<any[]>("locations?type=office");

            this.setState({
                locations
            }, () => this.search());
        }
        catch (e) {
            this.setState({
                isLoading: e.message
            });
        }
    }

    public render() {
        let table;

        if (this.state.isLoading === true) {
            table = <div className="spinner-center lds-dual-ring" />;
        }
        else if (typeof this.state.isLoading === "string") {
            table = <span className="error-text">{this.state.isLoading}</span>;
        }
        else {
            let tbody;

            if (this.state.appointments.length === 0) {
                tbody = (
                    <tr>
                        <td colSpan={11}>No appointments found</td>
                    </tr>
                );
            }
            else {
                tbody = this.state.appointments.map(a => (
                    <tr key={a.id} className={`clickable ${this.getRowColorClassName(a.billing)}`} onClick={() => this.goAppointment(a.id)}>
                        <td data-caption="Date: ">{formatDateLocal(a.appointmentTimestamp)}</td>
                        <td className="wrap" data-caption="Name: ">{formatName(a.patient.name)}</td>
                        <td className="col-center" data-caption="Chart: ">{a.patient.chartNumber}</td>
                        <td data-caption="Procedure: ">Uro</td>
                        <td className="wrap" data-caption="Insurance: ">{a.patient.insuranceProvider}</td>
                        <td className={"col-right-vt inline " + this.getColorClassName("cell", a.billing && a.billing.wcasStatus)} data-caption="WCAS Billed: ">{(a.billing && a.billing.wcasBilled) ? `${(+a.billing.wcasBilled).toLocaleString(undefined, { currency: "USD", style: "currency" })}` : "-"}</td>
                        <td className={"col-right-vt inline " + this.getColorClassName("cell", a.billing && a.billing.wcasStatus)} data-caption="WCAS Collected: ">{(a.billing && a.billing.wcasCollected) ? `${(+a.billing.wcasCollected).toLocaleString(undefined, { currency: "USD", style: "currency" })}` : "-"}</td>
                        <td className={"col-right-vt inline " + this.getColorClassName("cell", a.billing && a.billing.surgeonStatus)} data-caption="Surgeon's Fees Billed: ">{(a.billing && a.billing.surgeonsFeesBilled) ? `${(+a.billing.surgeonsFeesBilled).toLocaleString(undefined, { currency: "USD", style: "currency" })}` : "-"}</td>
                        <td className={"col-right-vt inline " + this.getColorClassName("cell", a.billing && a.billing.surgeonStatus)} data-caption="Surgeon's Fees Collected: ">{(a.billing && a.billing.surgeonsFeesCollected) ? `${(+a.billing.surgeonsFeesCollected).toLocaleString(undefined, { currency: "USD", style: "currency" })}` : "-"}</td>
                        <td className="wrap" data-caption="Last note: ">{truncateEllipse(a.lastNote ? a.lastNote : "", 45)}</td>
                    </tr>
                ));
            }

            table = (
                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Name</th>
                            <th>Chart #</th>
                            <th>Procedure</th>
                            <th>Insurance</th>
                            <th className="col-center th-font-tiny">WCAS<br />Billed</th>
                            <th className="col-center col-center th-font-tiny">WCAS<br />Collected</th>
                            <th className="col-center th-font-tiny">Surgeon's<br />fees<br />billed</th>
                            <th className="col-center th-font-tiny">Surgeon's<br />fees<br />collected</th>
                            <th>Last Note</th>
                        </tr>
                    </thead>
                    <thead className="table-vertical-head-small">
                        <tr>
                            <th>Surgery Appointments</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tbody}
                    </tbody>
                </table>
            );
        }

        return (
            <div className="viewer-root with-actionbar">
                <div className="filter-control-box">
                    <DelayInput type="search" timeout={500} className="fill-space" value={this.state.searchText} name="searchText" onChangeDelayed={this.handleElementChange} placeholder="Search..." />
                    <div className="control-group">
                        <span>Start Date:</span>
                        <DelayInput type="date" timeout={500} value={this.state.searchStart} name="searchStart" onChangeDelayed={this.handleElementChange} />
                    </div>
                    <div className="control-group">
                        <span>End Date:</span>
                        <DelayInput type="date" timeout={500} value={this.state.searchEnd} name="searchEnd" onChangeDelayed={this.handleElementChange} />
                    </div>
                    <select value={this.state.searchStatusWcas} name="searchStatusWcas" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All WCAS Status</option>
                        <option value="unset">Unset</option>
                        <option value="billed">Billed Out</option>
                        <option value="never">Never Paid</option>
                        <option value="partial">Partially Paid</option>
                        <option value="full">Fully Paid</option>
                    </select>
                    <select value={this.state.searchStatusSurgeon} name="searchStatusSurgeon" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All Read Status</option>
                        <option value="unset">Unset</option>
                        <option value="billed">Billed Out</option>
                        <option value="never">Never Paid</option>
                        <option value="partial">Partially Paid</option>
                        <option value="full">Fully Paid</option>
                    </select>
                    <select value={this.state.searchLocation} name="searchLocation" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All Locations</option>
                        {this.state.locations.map(a => <option key={a.id} value={a.id}>{a.shortName || a.name}</option>)}
                    </select>
                </div>
                {table}
                &nbsp;
                <div className="action-panel action-panel-noflip">
                    <button className="button push-right text-with-icon" onClick={this.previousPage} disabled={this.state.searchOffset === 0}><i className="icon-angle-left" /><span>Previous</span></button>
                    <button className="button text-with-icon" onClick={this.nextPage} disabled={this.state.appointments.length < PAGE_SIZE}><span>Next</span><i className="icon-angle-right" /></button>
                </div>
            </div>
        );
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            searchOffset: 0,

            [name]: value
        }, () => this.search());
    }

    private async search() {
        try {
            this.setState({
                isLoading: true
            });

            this.saveSearchState();

            const parameters: string[] = [
                `limit=${PAGE_SIZE}`,
                `offset=${this.state.searchOffset}`,
                "mode=insurance",
                "sort_by=date_for",
                "sort=desc",
                "status=complete",
                `type=${this.state.type}`
            ];
            if (this.state.searchLocation) {
                parameters.push(`location=${this.state.searchLocation}`);
            }
            if (this.state.searchEnd) {
                const beforeDt = DateTime.fromISO(this.state.searchEnd).endOf("day").plus({ millisecond: 1 });
                parameters.push(`before=${beforeDt.toISODate()}`);
            }
            if (this.state.searchStart) {
                parameters.push(`after=${this.state.searchStart}`);
            }
            if (this.state.searchText) {
                parameters.push(`q=${this.state.searchText}`);
            }
            if (this.state.searchStatus) {
                parameters.push(`insurance_status=${this.state.searchStatus}`);
            }
            if (this.state.searchStatusWcas) {
                parameters.push(`insurance_status_clinic=${this.state.searchStatusWcas}`);
            }
            if (this.state.searchStatusSurgeon) {
                parameters.push(`insurance_status_read=${this.state.searchStatusSurgeon}`);
            }

            var data = await apiGet<any[]>(`appointments/summary?${parameters.join("&")}`);

            this.setState({
                isLoading: false,

                appointments: data
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || e || "Couldn't load data"
            });
        }
    }

    private previousPage() {
        this.setState(s => ({
            searchOffset: Math.max(s.searchOffset - PAGE_SIZE, 0)
        }), () => this.search());
    }

    private nextPage() {
        this.setState(s => ({
            searchOffset: s.searchOffset + PAGE_SIZE
        }), () => this.search());
    }

    private goAppointment(id: string) {
        this.props.history.push(`/appointments/${id}/insurance`);
    }

    private saveSearchState() {
        const location = this.props.location;
        location.state = {
            searchOffset: this.state.searchOffset,
            searchLocation: this.state.searchLocation,
            searchStart: this.state.searchStart,
            searchEnd: this.state.searchEnd,
            searchText: this.state.searchText,
            searchStatusWcas: this.state.searchStatusWcas,
            searchStatusSurgeon: this.state.searchStatusSurgeon,
        };

        this.props.history.replace(location);
    }

    private getRowColorClassName(billing: any) {
        if (!billing)
            return "";

        if (billing.wcasStatus === billing.surgeonStatus) {
            return this.getColorClassName("row", billing.wcasStatus)
        }

        return "";
    }

    private getColorClassName(kind: "row" | "cell", status?: number) {
        if (!status)
            return "";

        if (status === 1) {
            return `${kind}-red`;
        }
        if (status === 3) {
            return `${kind}-green`;
        }
        if (status >= 4) {
            return `${kind}-blue`;
        }
        if (status >= 2) {
            return `${kind}-yellow`;
        }

        return "";
    }
}

export default withTitle("Office Appointments - Insurance", AppointmentInsuranceListView);